import {
  ArrowLeftOutlined,
  DeleteOutlined,
  EditOutlined,
  MoreOutlined,
  PlusOutlined
} from '@ant-design/icons';
import {
  Breadcrumb,
  Button,
  Dropdown,
  Form,
  Input,
  Menu,
  message,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography
} from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from '../../api';

const { Title, Text } = Typography;
const { Option } = Select;
const { Search } = Input;

const SpaceMemberManage = () => {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [members, setMembers] = useState([]);
  const [users, setUsers] = useState([]);
  const [spaceInfo, setSpaceInfo] = useState(null);
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [currentMember, setCurrentMember] = useState(null);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [searchUser, setSearchUser] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const searchInput = useRef(null);

  // 角色选项
  const roleOptions = [
    { value: 'owner', label: '所有者', color: 'red' },
    { value: 'admin', label: '管理员', color: 'orange' },
    { value: 'member', label: '成员', color: 'blue' },
    { value: 'viewer', label: '访客', color: 'green' }
  ];

  // 获取空间信息
  const fetchSpaceInfo = async () => {
    try {
      const response = await axios.get(`/api/document-groups/${groupId}/`);
      setSpaceInfo(response.data);
    } catch (error) {
      message.error('获取空间信息失败');
    }
  };

  // 获取成员列表
  const fetchMembers = async (page = 1, pageSize = 10, userKeyword = '', role = '') => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/document-groups/${groupId}/members/`, {
        params: {
          page,
          page_size: pageSize,
          search: userKeyword,
          role: role || undefined
        }
      });
      setMembers(response.data.results || []);
      setPagination({
        current: page,
        pageSize,
        total: response.data.count || 0
      });
    } catch (error) {
      message.error('获取成员列表失败');
    } finally {
      setLoading(false);
    }
  };

  // 获取用户列表（用于添加成员）
  const fetchUsers = async () => {
    try {
      const response = await axios.get('/api/users/', {
        params: {
          page_size: 1000 // 获取所有用户
        }
      });
      
      // 兼容不同的返回格式：分页格式 {results: [...], count: ...} 或直接数组 [...]
      let userList = [];
      if (response.data && Array.isArray(response.data)) {
        // 如果返回的是直接数组
        userList = response.data;
      } else if (response.data && response.data.results && Array.isArray(response.data.results)) {
        // 如果返回的是分页格式
        userList = response.data.results;
      }
      
      console.log('获取到的用户列表:', userList);
      setUsers(userList);
    } catch (error) {
      console.error('获取用户列表失败:', error);
      message.error('获取用户列表失败');
    }
  };

  // 添加成员
  const handleAddMember = async (values) => {
    try {
      await axios.post(`/api/document-groups/${groupId}/members/`, {
        document_group_id: parseInt(groupId),
        user_id: values.user_id,
        role: values.role
      });
      message.success('添加成员成功');
      setAddModalVisible(false);
      form.resetFields();
      fetchMembers(pagination.current, pagination.pageSize);
      fetchSpaceInfo(); // 刷新空间信息
    } catch (error) {
      if (error.response?.data?.error) {
        message.error(error.response.data.error);
      } else {
        message.error('添加成员失败');
      }
    }
  };

  // 编辑成员角色
  const handleEditMember = async (values) => {
    try {
      await axios.put(`/api/document-groups/${groupId}/members/${currentMember.id}/`, {
        document_group_id: parseInt(groupId),
        user_id: currentMember.user.id,
        role: values.role
      });
      message.success('更新成员角色成功');
      setEditModalVisible(false);
      editForm.resetFields();
      setCurrentMember(null);
      fetchMembers(pagination.current, pagination.pageSize);
    } catch (error) {
      if (error.response?.data?.error) {
        message.error(error.response.data.error);
      } else {
        message.error('更新成员角色失败');
      }
    }
  };

  // 删除成员
  const handleDeleteMember = async (memberId) => {
    try {
      await axios.delete(`/api/document-groups/${groupId}/members/${memberId}/`);
      message.success('删除成员成功');
      fetchMembers(pagination.current, pagination.pageSize);
      fetchSpaceInfo(); // 刷新空间信息
    } catch (error) {
      if (error.response?.data?.error) {
        message.error(error.response.data.error);
      } else {
        message.error('删除成员失败');
      }
    }
  };

  // 打开编辑模态框
  const showEditModal = (member) => {
    setCurrentMember(member);
    editForm.setFieldsValue({
      role: member.role
    });
    setEditModalVisible(true);
  };

  // 过滤事件
  const handleUserSearch = (selectedKeys, confirm) => {
    confirm();
    setSearchUser(selectedKeys[0]);
    fetchMembers(1, pagination.pageSize, selectedKeys[0], filterRole);
  };
  const handleRoleFilter = (value) => {
    setFilterRole(value);
    fetchMembers(1, pagination.pageSize, searchUser, value);
  };

  // 前端过滤（如后端不支持模糊搜索时可用）
  const filteredMembers = searchUser
    ? members.filter(m => m.user && (
        m.user.username?.toLowerCase().includes(searchUser.toLowerCase()) ||
        m.user.email?.toLowerCase().includes(searchUser.toLowerCase())
      ))
    : members;

  // 表格列定义
  const columns = [
    {
      title: '用户',
      key: 'user',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{record.user.username}</div>
          <div style={{ color: '#666', fontSize: '12px' }}>{record.user.email}</div>
        </div>
      )
    },
    {
      title: '角色',
      key: 'role',
      render: (_, record) => {
        const roleOption = roleOptions.find(option => option.value === record.role);
        return (
          <Tag color={roleOption?.color}>
            {roleOption?.label || record.role_display}
          </Tag>
        );
      }
    },
    {
      title: '加入时间',
      key: 'joined_at',
      render: (_, record) => new Date(record.joined_at).toLocaleString()
    },
    {
      title: '添加者',
      key: 'added_by',
      render: (_, record) => record.added_by?.username || '-'
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => {
        const menu = (
          <Menu>
            <Menu.Item
              key="edit"
              icon={<EditOutlined />}
              disabled={record.role === 'owner'}
              onClick={() => showEditModal(record)}
            >
              编辑角色
            </Menu.Item>
            <Menu.Item key="delete" icon={<DeleteOutlined />} disabled={record.role === 'owner'}>
              <Popconfirm
                title="确定要删除此成员吗？"
                onConfirm={() => handleDeleteMember(record.id)}
                okText="确定"
                cancelText="取消"
                disabled={record.role === 'owner'}
              >
                <span style={{ color: '#ff4d4f' }}>删除</span>
              </Popconfirm>
            </Menu.Item>
          </Menu>
        );
        return (
          <Dropdown overlay={menu} trigger={["click"]}>
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        );
      }
    }
  ];

  // 过滤已添加的用户
  const getAvailableUsers = () => {
    const memberUserIds = members.map(member => member.user.id);
    return users.filter(user => !memberUserIds.includes(user.id));
  };

  const handleAddMemberModalOpen = () => {
    setAddModalVisible(true);
    console.log('users:', users);
    console.log('members:', members);
  };

  useEffect(() => {
    if (groupId) {
      fetchSpaceInfo();
      fetchMembers();
      fetchUsers();
    }
  }, [groupId]);

  return (
    <div style={{ padding: '24px' }}>
      {/* 面包屑导航 */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16, minHeight: 32 }}>
        <Button
          type="link"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/space')}
          style={{ padding: 0, height: 28, display: 'flex', alignItems: 'center' }}
        >
          <span style={{ fontSize: 16, verticalAlign: 'middle' }}>{spaceInfo ? spaceInfo.name : '空间'}</span>
        </Button>
      </div>

      {/* 操作栏：搜索、筛选、添加成员 */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 8, flex: 1 }}>
          <Input.Search
            placeholder="搜索用户名/邮箱"
            allowClear
            style={{ width: 220 }}
            value={searchUser}
            onChange={e => setSearchUser(e.target.value)}
            onSearch={value => fetchMembers(1, pagination.pageSize, value, filterRole)}
          />
          <Select
            style={{ width: 140 }}
            placeholder="按角色筛选"
            value={filterRole || undefined}
            onChange={value => {
              setFilterRole(value);
              fetchMembers(1, pagination.pageSize, searchUser, value);
            }}
            allowClear
          >
            <Option value="">全部角色</Option>
            <Option value="owner">所有者</Option>
            <Option value="admin">管理员</Option>
            <Option value="member">成员</Option>
            <Option value="viewer">访客</Option>
          </Select>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAddMemberModalOpen}
        >
          添加成员
        </Button>
      </div>

      {/* 成员列表 */}
      <Table
        columns={columns}
        dataSource={filteredMembers}
        rowKey="id"
        loading={loading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
          onChange: (page, pageSize) => fetchMembers(page, pageSize)
        }}
      />

      {/* 添加成员模态框 */}
      <Modal
        title="添加成员"
        open={addModalVisible}
        onCancel={() => {
          setAddModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={500}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleAddMember}
        >
          <Form.Item
            name="user_id"
            label="选择用户"
            rules={[{ required: true, message: '请选择用户' }]}
          >
            <Select
              placeholder="请选择要添加的用户"
              showSearch
              filterOption={(input, option) =>
                option.children && option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {getAvailableUsers().map(user => (
                <Option key={user.id} value={user.id}>
                  {user.username}（{user.email}）
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="role"
            label="分配角色"
            rules={[{ required: true, message: '请选择角色' }]}
          >
            <Select placeholder="请选择角色">
              {roleOptions.map(option => (
                <Option key={option.value} value={option.value}>
                  <Tag color={option.color}>{option.label}</Tag>
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                添加
              </Button>
              <Button onClick={() => {
                setAddModalVisible(false);
                form.resetFields();
              }}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 编辑成员模态框 */}
      <Modal
        title="编辑成员角色"
        open={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
          editForm.resetFields();
          setCurrentMember(null);
        }}
        footer={null}
        width={500}
      >
        {currentMember && (
          <Form
            form={editForm}
            layout="vertical"
            onFinish={handleEditMember}
          >
            <Form.Item label="用户">
              <Input
                value={currentMember.user.username}
                disabled
              />
            </Form.Item>

            <Form.Item
              name="role"
              label="角色"
              rules={[{ required: true, message: '请选择角色' }]}
            >
              <Select placeholder="请选择角色">
                {roleOptions.map(option => (
                  <Option key={option.value} value={option.value}>
                    <Tag color={option.color}>{option.label}</Tag>
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit">
                  更新
                </Button>
                <Button onClick={() => {
                  setEditModalVisible(false);
                  editForm.resetFields();
                  setCurrentMember(null);
                }}>
                  取消
                </Button>
              </Space>
            </Form.Item>
          </Form>
        )}
      </Modal>
    </div>
  );
};

export default SpaceMemberManage;
